import { graphql } from '@@fire-b2b/graphql/codegen'
import type { FrontendUserQuery } from '@@fire-b2b/graphql/codegen/graphql'
import { gqlExecute, type GQLResult } from '@fire-b2b/utils/gql'
import { defineStore } from 'pinia'

export type ActiveCustomer = FrontendUserQuery['frontenduser']['activeCustomer']
export type Country = ActiveCustomer['country']
export type Currency = ActiveCustomer['currency']
export type FrontendUser = FrontendUserQuery['frontenduser']

interface UserInfo {
  id: number
  email: string
  username: string
  firstname: string
  lastname: string
  csrfToken: string
  authKey: string
}

export const useUserInfoStore = defineStore('userInfo', () => {
  const t = useTranslate()

  const userInfo = ref<UserInfo>({} as UserInfo)
  const customer = ref<ActiveCustomer | null>(null)

  const hello = (name?: string) => (name ? t('common.helloName', { name }) : t('common.hello'))

  const fullName = computed(() => [userInfo.value.firstname, userInfo.value.lastname].filter(Boolean).join(' '))
  const email = computed(() => userInfo.value.email)
  const initials = computed(() => {
    const { firstname, lastname } = userInfo.value
    const first = firstname?.charAt(0) || ''
    const last = lastname?.charAt(0) || ''
    return `${first}${last}`.toUpperCase()
  })
  const initialLastName = computed(() => {
    const { firstname, lastname } = userInfo.value
    const firstInitial = firstname?.charAt(0)
    return firstInitial ? `${firstInitial}. ${lastname}` : (lastname ?? '')
  })
  const firstNameInitial = computed(() => {
    const { firstname, lastname } = userInfo.value
    const lastNameInitial = lastname.charAt(0)
    return lastNameInitial ? `${firstname} ${lastNameInitial}.` : (firstname ?? '')
  })
  const countryCode = computed(() => customer.value?.country?.countryCode ?? 'DE')
  const currencyIso = computed(() => customer.value?.currency?.isoCode ?? 'EUR')
  const currencyRrpIso = computed(() => customer.value?.currencyRrp?.isoCode ?? currencyIso.value)

  async function loadFrontendUser(): Promise<GQLResult<void>> {
    const result = await gqlExecute({
      query: graphql(`
        query FrontendUser {
          frontenduser {
            id
            salutation
            firstname
            lastname
            email
            activeCustomer {
              customerId
              customerNumber
              name
              country {
                countryId
                countryCode
                name
                termsOfService
                popupTermsOfService
                imprint
                privacyPolicy
              }
              currency {
                currencyId
                title
                isoCode
              }
              currencyRrp {
                currencyId
                title
                isoCode
              }
            }
          }
        }
      `),
      variables: undefined,
    })

    if (isErr(result)) {
      return result
    }

    const frontendData = result.data.frontenduser

    if (frontendData) {
      userInfo.value = {
        ...userInfo.value,
        id: frontendData.id ?? -1,
        email: frontendData.email ?? '',
        firstname: frontendData.firstname ?? '',
        lastname: frontendData.lastname ?? '',
      }
    }

    customer.value = frontendData.activeCustomer ?? null

    return ok()
  }

  return {
    userInfo,
    customer,
    countryCode,
    currencyIso,
    currencyRrpIso,
    fullName,
    email,
    initials,
    initialLastName,
    firstNameInitial,
    helloUser: computed(() => hello(userInfo.value.firstname)),
    helloUserFullName: computed(() => hello(fullName.value)),
    getUserInfo: computed(() => userInfo.value),
    setUserInfo: (info: UserInfo) => {
      userInfo.value = info
    },
    loadFrontendUser,
    isCurrentUser: (frontendUserId: number): boolean => frontendUserId === userInfo.value.id,
  }
})
